import type { PackageType } from '../types/reference-data';

const NO_CARRIER_ID = '0';

export const STANDARD_PACKAGE_TYPE_ID = '3';

/**
 * Builds a composite key for a PackageType that is unique across carriers.
 * Multiple carriers can share the same packageTypeId (e.g., "3"), so the
 * raw ID alone is not suitable as a select option value.
 */
export const buildPackageId = (packageTypeId: string, isCustom: boolean, carrierId: string | null) =>
  `${packageTypeId}-${isCustom}-${carrierId ?? NO_CARRIER_ID}`;

export const buildPackageIdFromPackageType = (packageType: PackageType) =>
  buildPackageId(packageType.packageTypeId, packageType.isCustom, packageType.carrierId);

export const STANDARD_PACKAGE_ID = buildPackageId(STANDARD_PACKAGE_TYPE_ID, false, null);

const parseCarrierId = (carrierId: string | null) => {
  if (!carrierId || carrierId === NO_CARRIER_ID || carrierId === 'null' || carrierId === 'undefined') {
    return null;
  }

  return carrierId;
};

const NO_PACKAGE_ID_INFO = {
  packageTypeId: undefined,
  isCustom: false,
  carrierId: null,
};

/**
 * Parses a composite package id into its parts, deriving `isCustom` and
 * `isCarrier` in the same pass so callers never need to re-scan the string.
 *
 * The composite id format is `${packageTypeId}-${isCustom}-${carrierId ?? '0'}`.
 * Missing or malformed ids (no packageId, or fewer than the two expected
 * dashes) fall back to `NO_PACKAGE_ID_INFO` instead of slicing garbage out
 * of the string, so callers never need their own null/malformed check.
 */
export const parsePackageId = (packageId?: string | null) => {
  if (!packageId) {
    return NO_PACKAGE_ID_INFO;
  }

  const firstDash = packageId.indexOf('-');
  const secondDash = packageId.indexOf('-', firstDash + 1);

  if (firstDash === -1 || secondDash === -1) {
    return NO_PACKAGE_ID_INFO;
  }

  const rawCarrierId = packageId.slice(secondDash + 1);
  const carrierId = parseCarrierId(rawCarrierId);

  return {
    packageTypeId: packageId.slice(0, firstDash),
    isCustom: packageId.slice(firstDash + 1, secondDash) === 'true',
    carrierId,
  };
};

/**
 * Returns true when the composite package id represents a carrier-supplied
 * package (e.g. USPS Flat Rate Box).
 */
export const isCarrierPackageId = (packageId?: string): boolean => {
  if (!packageId) {
    return false;
  }

  const { carrierId } = parsePackageId(packageId);

  return Boolean(carrierId);
};
